/**
 * The tagged JSON codec.
 *
 * Master plan §2 D1.
 *
 * Money is `bigint` minor units everywhere in this system, and JSON has no
 * integer type that survives one — `JSON.stringify` refuses outright rather than
 * lying, which is the correct behaviour and the reason this codec exists.
 *
 * It lives here, alone, because three places need the SAME tag and they are not
 * allowed to disagree about it: the durable outbox store encodes with it, the
 * audit log encodes with it, and the canonical signer REFUSES it as a data key
 * so that `{"$gfBigInt":"5"}` and the bigint `5n` cannot hash to the same bytes.
 * Three copies of a constant whose whole job is agreement is three chances to
 * stop agreeing.
 */

/** TAGGED rather than written as a bare string, so a field that really is text
 *  and happens to look numeric is not resurrected as a bigint. */
export const BIGINT_TAG = "$gfBigInt";

export interface TaggedBigInt {
  readonly [BIGINT_TAG]: string;
}

export const isTaggedBigInt = (value: unknown): value is TaggedBigInt =>
  typeof value === "object" &&
  value !== null &&
  Object.keys(value).length === 1 &&
  typeof (value as Record<string, unknown>)[BIGINT_TAG] === "string";

/** Exact both ways: whatever the caller encoded is what comes back. */
export function encodePayload(value: unknown): string {
  return JSON.stringify(value, (_key, v: unknown) =>
    typeof v === "bigint" ? { [BIGINT_TAG]: v.toString() } : v,
  );
}

export function decodePayload<T>(text: string): T {
  return JSON.parse(text, (_key, v: unknown) =>
    isTaggedBigInt(v) ? BigInt(v[BIGINT_TAG]) : v,
  ) as T;
}

/** One level, for a flat record going into a jsonb column. */
export const encodeTaggedRecord = (
  record: Readonly<Record<string, unknown>>,
): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(record).map(([k, v]) => [
      k,
      typeof v === "bigint" ? { [BIGINT_TAG]: v.toString() } : v,
    ]),
  );

export const decodeTaggedRecord = (
  record: Readonly<Record<string, unknown>>,
): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(record).map(([k, v]) => [k, isTaggedBigInt(v) ? BigInt(v[BIGINT_TAG]) : v]),
  );
